import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { htmlEncode } from '../../../utils/escape.utils';
import { SummaryConstants } from '../SummaryConstants';
import { translateFn } from '../../../utils/translator.helper';

export class SummaryPanelView extends BaseWebComponentView {
  public static readonly MAX_NR_OF_ATTACK_TYPES: number = 2;
  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$scoreCounter = this.shadow.querySelector('#summary-counter');
    this.dom.$scoreDescription = this.shadow.querySelector('#score-description');
    this.dom.$created = this.shadow.querySelector('#created');
    this.dom.$summaryTitle = this.shadow.querySelector('#summary-title');
    this.dom.$rootCauseTitle = this.shadow.querySelector('#root-cause-title');
    this.dom.$lastUpdated = this.shadow.querySelector('#last-updated');
    this.dom.$attackTypes = this.shadow.querySelector('#attack-types');
    this.dom.$visibleAttackTypesWrapper = this.shadow.querySelector('#visible-attack-types-wrapper');
    this.dom.$moreAttackTypesWrapper = this.shadow.querySelector('#more-attack-types-wrapper');
    this.dom.$narrative = this.shadow.querySelector('#narrative');
    this.dom.$reasoning = this.shadow.querySelector('#reasoning');
    this.dom.$incidentReportBtn = this.shadow.querySelector('#incident-report');

    // Dom elements for the other/more attack types text/card
    this.dom.$otherAttackTypesText = this.shadow.querySelector('#other-attack-types-text');
    this.dom.$moreAttackTypesCard = this.shadow.querySelector('#more-attack-types-card');
    this.dom.$moreAttackTypesTitle = this.shadow.querySelector('#more-attack-types-title');
    this.dom.$moreAttackTypesList = this.shadow.querySelector('#more-attack-types-list');
    this.dom.$moreAttackTypesCloseButton = this.shadow.querySelector('#more-attack-types-close-button');

    this.dom.$createdTooltip = this.shadow.querySelector('#created-tooltip');
    this.dom.$lastUpdatedTooltip = this.shadow.querySelector('#last-updated-tooltip');
  }

  getSummaryElements() {
    this.dom.$gzLinkComponents = this.shadow.querySelectorAll('gz-link');
  }

  getDefaultTextForSummary(type: string): string {
    if (type === SummaryConstants.NARRATIVE) {
      return this.dependencies.translateFn('Not enough info to provide an incident summary.');
    }

    if (type === SummaryConstants.REASONING) {
      return this.dependencies.translateFn('Not enough data to establish how these actions were possible.');
    }

    return this.dependencies.translateFn('Not enough info');
  }

  buildAttackTypes(attackTypes: Array<string>): void {
    const visibleAttackTypes = attackTypes.slice(0, SummaryPanelView.MAX_NR_OF_ATTACK_TYPES);

    this.dom.$visibleAttackTypesWrapper.innerHTML = visibleAttackTypes.map(htmlEncode).join('<br />');

    if (attackTypes.length <= SummaryPanelView.MAX_NR_OF_ATTACK_TYPES) {
      return;
    }

    const nrOthers = attackTypes.length - SummaryPanelView.MAX_NR_OF_ATTACK_TYPES;
    if (nrOthers === 1) {
      this.dom.$otherAttackTypesText.innerText = this.dependencies.translateFn('+ 1 OTHER');
    } else {
      this.dom.$otherAttackTypesText.innerText = this.dependencies.translateFn('+ {nrOthers} OTHERS', { nrOthers });
    }
    this.dom.$moreAttackTypesTitle.innerText = this.dependencies.translateFn('Type of Attack');
    // Attack types are not translated, this may change in the future
    this.dom.$moreAttackTypesList.innerHTML = attackTypes.map(htmlEncode).join('</br >');
  }

  buildView(data: any) {
    if (!data) {
      this.dom.$wrapper.innerHTML = '';

      return;
    }

    // Translated titles
    this.dom.$scoreDescription.innerText = this.dependencies.translateFn('Incident Severity Score');
    this.dom.$summaryTitle.innerText = this.dependencies.translateFn('SUMMARY');
    this.dom.$rootCauseTitle.innerText = this.dependencies.translateFn('ROOT CAUSE');

    // Translated labels
    this.dom.$created.label = this.dependencies.translateFn('Created:');
    this.dom.$lastUpdated.label = this.dependencies.translateFn('Last updated:');
    this.dom.$attackTypes.label = this.dependencies.translateFn('Type of attack:');

    // Translations to empty states
    this.dom.$narrative.innerText = this.dependencies.translateFn(' Not enough info to provide an incident summary.');
    this.dom.$reasoning.innerText = this.dependencies.translateFn(
      ' Not enough data to establish how these actions were possible.'
    );

    this.dom.$incidentReportBtn.innerText = translateFn('Generate report');

    this.shadow.host.style.setProperty('--severity-color', data.response.severityColor);
    this.dom.$scoreCounter.target = htmlEncode(data.response.incidentScore);
    this.dom.$created.innerHTML = htmlEncode(data.response.created);
    this.dom.$createdTooltip.tooltipText = htmlEncode(data.response.created);
    this.dom.$lastUpdated.innerHTML = htmlEncode(data.response.lastUpdated);
    this.dom.$lastUpdatedTooltip.tooltipText = htmlEncode(data.response.lastUpdated);
    this.buildAttackTypes(data.response.attackTypes);

    this.dom.$narrative.innerHTML = this.parsePreProcessedSummaryText('narrative', data.response.narrative).join('');
    this.dom.$reasoning.innerHTML = this.parsePreProcessedSummaryText('reasoning',data.response.reasoning).join('');

    this.getSummaryElements();

    this.dom.$gzLinkComponents.forEach(gzlink => {
      gzlink.showIcon = false;

      gzlink.addEventListener('click', () => {
        gzlink.dispatchEvent(
          new CustomEvent(SummaryConstants.OpenSidePanel, {
            bubbles: true,
            composed: true,
            detail: {
              type: gzlink.className,
              elements: gzlink.getAttribute('elements'),
            },
          })
        );
      });
    });
  }

  parsePreProcessedSummaryText(type: 'narrative' | 'reasoning', summaryText: string[][] | string[]): string[] {
   summaryText.forEach((element) => {
     if (typeof element === 'string') {
        return;
      }

     /*
      The regex captures:
      - `{LINK}`: The opening tag for the link.
      - `{ELEMENTSGROUP}` or `{ELEMENTS}`: The type of elements group.
      - `(.*?)`: The encoded JSON string of elements, required for pivoting in graph / alert panel
      - `{\/\1}`: The closing tag for the elements group, which matches the opening tag type.
      - `(.*?)`: The content between the tags, which is the displayed value in narrative.
      */
     const narrativeRegex = /\{LINK}\{(ELEMENTSGROUP|ELEMENTS)}(.*?)\{\/\1}(.*?)\{\/LINK}/g

     /*
      The regex captures:
       - `{TEXT}`: The opening tag for the text.
       - `(.*?)`: The content between the tags, which is the displayed value in narrative.
       - `{\/TEXT}`: The closing tag for the text.
       - This is used to wrap text in gz-text component. (for some ERA phrases)
     */
     const eraRegex = /\{TEXT}(.*?){\/TEXT}/g

      element.forEach((phrase, index) => {
        element[index] = phrase.replace(
          narrativeRegex,
          (_, groupType, elementsEncodedJson, contentBetweenTags) => {
            try {
              const encodedElements = htmlEncode(elementsEncodedJson).replace(/"/g, '&quot;');
              const cls = (groupType === 'ELEMENTSGROUP') ? 'elements-group' : 'element';
              return `<gz-link class="${cls}" elements="${encodedElements}" type="quiet" color="primary" fontsize="12">
                    ${htmlEncode(contentBetweenTags)}
                </gz-link>`;
            } catch (error) {
              console.error("Invalid ELEMENTS format:", error);
              return `${contentBetweenTags}`; // Use only content between tags if not valid
            }
          }
        )
        element[index] = element[index].replace(
          eraRegex,
          (_, contentBetweenTags) => {
            return `<gz-text fontSize="12" style="display:inline;">${htmlEncode(contentBetweenTags)}</gz-text>`;
          }
        )
      });
    });
    const finalSummary: string[] = [];
    summaryText.forEach((element, index) => {
      if (element.length === 0) {
        return;
      }

      finalSummary.push(`<div paragraph-location="${index}">`);
      if (typeof element === 'string') {
        finalSummary.push(element);
      } else {
        element.forEach(phrase => {
          finalSummary.push(phrase);
        });
      }
      finalSummary.push('</div>');
    });

    return finalSummary.length > 0
      ? finalSummary
      : [`<div paragraph-location="${0}">`, this.getDefaultTextForSummary(type), '</div>'];
  }
}
